import { useEffect, useRef } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { HorizonFooter } from '~/components/HorizonFooter'
import { Search } from '~/components/Search'
import { Masthead } from '~/components/Masthead'

import { useBarsHidden } from '~/components/useBars'
import { Avatar } from '~/social/Avatar'
import { Notices } from '~/social/Notices'
import { useWaiting } from '~/social/waiting'

import { readAsReader, readingAsReader, useSite, useViewer } from './Viewer'

/**
 * The pages that draw the chrome frame instead of the footer.
 *
 * A trailing slash means a prefix — `/claim/<token>` and `/i/<token>` carry one. Listed here
 * rather than asked of the route, because the shell renders outside the outlet and has no
 * way to be told by the view inside it.
 */
const DOORS = ['/login', '/join', '/claim/', '/i/'] as const
import s from './Shell.module.css'

/**
 * The frame every view sits in, and the point of the whole port.
 *
 * The masthead is rendered here, outside the route outlet, so moving between views never
 * unmounts it: the bar is the same DOM node before and after, the page does not reload, and
 * nothing below it can disagree with it about where it sits.
 *
 * That was the complaint. Two documents each drawing their own copy of the same bar meant a
 * full reload on every move and the wordmark landing 22px lower on one than the other, which
 * is exactly how a reader can tell that nothing is being reused. Aligning the two copies
 * narrowed the seam; only this removes it.
 *
 * The canvas below is whatever the address says. Today that is the highlights; when the
 * calendar is ported it becomes a second view rather than a second page, and this file does
 * not change.
 */
export function Shell() {
  // Two hooks, deliberately. `useSite` answers with empty values before the fetch lands,
  // which is right for a footer — nothing to say yet reads as a footer with nothing to say.
  // It is wrong for the bar, where "nobody is signed in" and "I do not know yet" have to
  // stay apart or it says "Sign in" to somebody who is. `useViewer` keeps them apart.
  const navigate = useNavigate()
  const here = useLocation()

  /*
   * Which page this is, on the body, for the two rules that are about a route rather than
   * about a component.
   *
   * The masthead is one component on every page and cannot ask; a CSS module cannot reach
   * across files to a class it does not own. A body attribute is the one thing both halves
   * can see, and it says what the page *is* rather than what it should look like — which is
   * the difference between this and putting a class on the bar from here.
   */
  useEffect(() => {
    const route = here.pathname === '/calendar' ? 'calendar' : ''
    if (route) document.body.dataset.route = route
    else delete document.body.dataset.route
    return () => {
      delete document.body.dataset.route
    }
  }, [here.pathname])
  /*
   * The doors draw the chrome frame, and the footer is chrome as well — two pieces of the
   * same artwork on one screen, one of them a horizon along the bottom and the other a frame
   * around everything, arguing about which edge of the page you are looking at.
   *
   * The frame is the one that belongs here: these pages are a single centred card, and the
   * footer's colophon — last updated, the Instagram handles — is about the calendar, which
   * is not what somebody signing in is looking at.
   */
  const behindTheFrame = DOORS.some(door =>
    door.endsWith('/') ? here.pathname.startsWith(door) : here.pathname === door,
  )
  const me = useViewer()
  const { updatedAt, handles } = useSite()
  // Only asked for once somebody is signed in: a signed-out reader has no requests and the
  // fetch would 401 on every page load.
  const waiting = useWaiting(me ? me.id : null)
  // Out of the way going down, back the moment you turn round.
  const hidden = useBarsHidden()
  const mast = useRef<HTMLElement>(null)
  // Whether the editor is looking at the site as a reader. Read once so the banner, the
  // menu and the briefing button cannot disagree with each other within a render.
  const normie = readingAsReader()

  /*
   * The masthead's height, published as `--mast-h` for whatever sticks underneath it — the
   * calendar's toolbar today.
   *
   * Measured rather than written down. The bar is `clamp()`ed padding around a wordmark, a
   * search field and an avatar, so its height is a different number at every width and
   * changes again when the briefing badge appears. A constant would be right at one size.
   *
   * Zero while the masthead is away, so the thing below it rises into the space instead of
   * leaving a strip of calendar showing through where the bar used to be.
   */
  useEffect(() => {
    const bar = mast.current
    if (!bar) return
    const publish = () => {
      const height = hidden ? 0 : bar.getBoundingClientRect().height
      document.documentElement.style.setProperty('--mast-h', `${Math.round(height)}px`)
    }
    publish()
    const watch = new ResizeObserver(publish)
    watch.observe(bar)
    return () => watch.disconnect()
  }, [hidden])

  return (
    <>
      {/*
        * Outside the 1280px column, for the same reason the footer is: the bar is the top
        * edge of the window and a rule that stops at the column edge is a rule with black
        * either side of it on any screen wider than a laptop. `.bar` inside puts the
        * contents back on the column, so the text still lines up with the page below.
        */}
      <header ref={mast} className={hidden ? `${s.mast} ${s.away}` : s.mast}>
        <div className={s.bar}>
          {/*
            * Normie mode, said out loud.
            *
            * The switch takes away every control only the editor has, which is exactly what
            * makes it easy to forget you left it on — there is nothing missing that you can
            * see is missing. A strip is the cheapest thing that answers "why can I not add
            * this gig".
            *
            * Inside the header, so it goes and comes back with the bar rather than sitting
            * over the page on its own, and so `--mast-h` measures it — the calendar's own
            * sticky block would otherwise slide under it.
            *
            * The briefing count is here because it is nowhere else: the bar's button is
            * gone while the switch is on, and a pile of decisions you cannot see is worse
            * than one you have chosen not to look at. Pressing it turns the switch off and
            * lands on `/brief`, because `/brief` refuses anybody who is not curating — so
            * turning it off and staying put would leave you one more press from the thing
            * you just asked for.
            */}
          {normie ? (
            <div className={s.normie} role="status">
              <b>Normie mode</b>
              <span>seeing the site as a reader does</span>
              {waiting.briefing > 0 ? (
                <button type="button" onClick={() => readAsReader(false, '/brief')}>
                  {waiting.briefing.toLocaleString()} waiting in your briefing
                </button>
              ) : null}
              {waiting.tidy > 0 ? (
                <button type="button" onClick={() => readAsReader(false, '/tidy')}>
                  {waiting.tidy} names to judge
                </button>
              ) : null}
              <button type="button" onClick={() => readAsReader(false)}>
                Back to curating
              </button>
            </div>
          ) : null}
        {/* `undefined` while the answer is in flight, and the bar draws nothing rather
            than "Sign in" — which is the wrong answer for half the readers and was the
            whole of the bug this component had already been through once. */}
          <Masthead
            me={me ? me.name : null}
            // Their own face beside their own name. `Avatar` falls back to initials, so a
            // reader who has not uploaded one still gets a plate rather than a gap.
            face={me ? <Avatar accountId={me.id} name={me.name} has={me.hasAvatar} size="small" /> : null}
            pending={me === undefined}
            // The briefing, reachable from every page rather than only from the calendar's
            // own toolbar. Bernardo could not find the button, which is the honest reading
            // of "it does nothing": the only two were on `/calendar`.
            // Nought while the switch is on. The server has no idea about normie mode —
            // it is a way of looking at the page and not a fact about the account — so
            // `/waiting` keeps answering with the real number, and a Briefing button drawn
            // from it navigates to `/brief`, which sends anybody who is not curating to the
            // front door. Press Briefing, land on the front page: a button that does not
            // work, and the third time that sentence has been reported about this button.
            //
            // Masked here rather than in `useWaiting`, which is where it was for one commit
            // — the banner above needs the real count, and zeroing it at the source took it
            // away from the one place that has to say it.
            briefing={normie ? 0 : waiting.briefing}
            // Both queues go in normie mode, for the same reason: they are the editor's, and
            // the whole point of the switch is that nothing only they can do is on the page.
            tidy={normie ? 0 : waiting.tidy}
            onTidy={() => navigate('/tidy')}
            // No `state.background`: `/brief` is a page, not an address in `App`'s `OVER`,
            // and only an opener of a sheet carries one — `AppLink`'s rule. One passed here
            // stays on the `/brief` entry for as long as the entry lives, reloads included,
            // and a sheet-to-sheet opener unwraps `state.background` as "the page beneath":
            // given the briefing's location it would draw this page, not the briefing.
            //
            // `here` would also be the wrong page: this bar renders inside the routes drawn
            // *beneath* any open sheet, so it is the page under the sheet, not the sheet.
            onBriefing={() => navigate('/brief')}
            // Only for somebody signed in: a signed-out reader has no notices, and the
            // panel would 401 the moment it was opened. Not masked in normie mode — a
            // friend accepting your request is not editorial work, and it is the one badge
            // on this bar that everybody has.
            notices={me ? <Notices unread={waiting.notices} /> : null}
            mine={me ? `/u/${me.username}` : null}
            // The studio door. `me.isEditor` alone: `ViewerProvider` sets it false while the
            // editor is reading as a reader, so normie mode takes this away with the star and
            // the curate panel rather than needing a second condition here — which is the one
            // control normie mode must not miss, being the way to publish.
            studio={Boolean(me?.isEditor)}
            // Only the editor gets a menu behind their name; for everybody else it stays a
            // link, because a menu that opens to reveal one entry is worse than the entry.
            // `readingAsReader()` as well as `isEditor`: in normie mode the second is false,
            // and gating on it alone would take away the way back.
            {...(me && (me.isEditor || normie)
              ? { normie, onNormie: (on: boolean) => readAsReader(on) }
              : {})}
            search={
              <Search
                people={Boolean(me)}
                onGig={id => navigate(`/e/${id}`, { state: { background: here } })}
                // A gig opens over this page; an act is a page of its own and is not in
                // `OVER`, so it is passed nothing — the same rule as Briefing above.
                onArtist={id => navigate(`/a/${id}`)}
                onVenue={slug => navigate(`/v/${slug}`)}
                onGenre={genre => navigate(`/g/${encodeURIComponent(genre)}`)}
                onPerson={username => navigate(`/u/${username}`)}
                onEverything={q => navigate(`/search?q=${encodeURIComponent(q)}`)}
              />
            }
          />
        </div>
      </header>

      <div className={s.page}>
      {/*
        * The canvas. One element, one owner, and the view swaps inside it.
        *
        * It holds a screen's height whatever is in it, which is not decoration: the footer
        * is the shell's now, so it renders immediately while the view below the bar is
        * still fetching — and with nothing between them it appeared at the top of the
        * window for as long as that took, then jumped down. Reserving the space means the
        * footer is where it will end up from the first frame.
        */}
        <div className={s.canvas}>
          <Outlet />
        </div>
      </div>

      {/*
        * Here for the same reason the masthead is: it is the same on every page, and a route
        * that drew its own would be two footers that can disagree. It was also a `<div>`
        * inside `<main>`, so the document had a header, a nav and no footer landmark at all.
        *
        * Outside the 1280px column, so the artwork runs the width of the window. It is a
        * horizon — a band of sky along the bottom of the page — and a horizon that stops
        * 80px short of each edge is a picture of one rather than the thing itself. The
        * colophon inside it keeps its own gutter, so the type does not follow the picture
        * out to the edges.
        */}
      {behindTheFrame ? null : (
        <HorizonFooter
          updatedAt={updatedAt}
          handles={handles}
          /* `undefined` while the viewer is still being fetched, which draws neither state —
             offering a sign-in link and taking it away a moment later is worse than waiting. */
          signedIn={me === undefined ? undefined : me !== null}
        />
      )}
    </>
  )
}
